import React, { ChangeEvent, useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { ACCEPTED_DATA_TYPES, ACCEPTED_EXTENSIONS } from "src/stores/krediflow/ImportInvoiceFilesStore";
import { IInputWorkflowUploadFile } from "src/stores/krediflow/InvoiceTypes";
import { v4 as uuidv4 } from "uuid";
import styles from "./ImportInvoicesInput.module.css";
import { INVOICE_FILE_STATE, INVOICE_FILE_TYPE } from "src/stores/krediflow/InvoiceEnums";
import { Ticket } from "src/stores/TicketStore";
import { MessageType } from "src/components/notifications/Notifier";
import { useHistory } from "react-router-dom";
import { useUrlQuery } from "src/utils/Route";
import { Route, selectRoute } from "src/config/routes";

interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}

interface ImportInvoicesInputProps {
    handleClickManually: boolean;
}

const ImportInvoicesInputBase = (props: ImportInvoicesInputProps) => {
    const { t } = useTranslation();
    const { importInvoiceFilesStore, uiStore, authStore } = useContext(RootStoreContext);

    const history = useHistory();
    let urlQuery = useUrlQuery();
    const tabUrlQuery = urlQuery.get("tab");

    const { handleClickManually } = props;
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        importInvoiceFilesStore.setFileInputRef(fileInputRef.current);
    }, [importInvoiceFilesStore]);

    const onHandleChangeInput = useCallback(
        async (event: FileHTMLInputEvent) => {
            importInvoiceFilesStore.setAreFilesBeingImported(true);

            if (event?.target?.files) {
                const files = Array.from(event.target.files);

                if (files.length > 0) {
                    for (const file of files) {
                        const fileUrl = URL.createObjectURL(file);

                        const invoiceFile: IInputWorkflowUploadFile = {
                            fileId: uuidv4(),
                            invoiceFileId: uuidv4(),
                            name: file.name,
                            url: fileUrl,
                            type: INVOICE_FILE_TYPE.INPUTWORKFLOW_FILE,
                            mimetype: file.type,
                            extension: file.name.split(".").pop(),
                            fileDate: Ticket.formatDateForImage(new Date(file.lastModified)),
                            state: INVOICE_FILE_STATE.HAS_CHANGED,
                            size: file.size,
                            fileObject: file
                        };

                        const isPDFFile = ACCEPTED_DATA_TYPES.includes(file.type);

                        if (isPDFFile) {
                            if (!tabUrlQuery) {
                                const creditorsRoute = `${selectRoute(
                                    Route.creditors,
                                    authStore.user?.role
                                )}?tab=import`;
                                history.push(creditorsRoute);
                            }

                            await importInvoiceFilesStore.uploadInputWorkflowFileToBlobStorage(invoiceFile);
                        } else {
                            uiStore.printStatusMessage(
                                t("screens.kredi_flow.document_import.only_pdf_imports_allowed", {
                                    missing_fields_string: invoiceFile.name
                                }),
                                MessageType.ERROR
                            );
                        }
                    }
                }
            }

            importInvoiceFilesStore.setIsDraggingOverDropZone(false);
            importInvoiceFilesStore.setAreFilesBeingImported(false);
        },
        [t, uiStore, importInvoiceFilesStore, authStore.user?.role, history, tabUrlQuery]
    );

    return (
        <input
            ref={fileInputRef}
            id={styles.InvoicePreviewInput}
            style={{ display: handleClickManually ? "none" : "" }}
            type="file"
            onChange={onHandleChangeInput}
            accept={ACCEPTED_DATA_TYPES.concat(ACCEPTED_EXTENSIONS).join(",")}
            multiple
        />
    );
};

export const ImportInvoicesInput = Sentry.withProfiler(observer(ImportInvoicesInputBase));
